/**
 * payment 앱 전용 토스트 시스템.
 *
 * 사용:
 *   const { show } = useToast()
 *   show('변경 실패했어요')
 *
 * 시스템 에러(Sentry 전송)는 reportError로 별도 처리하고, 이 토스트는 사용자에게
 * 보여줄 짧은 안내만 담당한다. variant 분기 없는 단일 디자인 (Figma 18850:202286).
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import type { ReactNode } from 'react'

import { ToastItemElement, ToastViewportContainer } from './styles'

interface ToastItem {
  id: string
  message: string
  exiting?: boolean
}

interface ToastContextValue {
  show: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_DURATION_MS = 3000
const TOAST_EXIT_MS = 700

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const idCounterRef = useRef(0)

  const show = useCallback((message: string) => {
    idCounterRef.current += 1
    const id = `toast-${idCounterRef.current}`
    setToasts((prev) => [...prev, { id, message }])

    // 노출 시간 후 exit 애니메이션 시작 → 애니메이션 끝나면 DOM 제거
    const exitTimer = setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)))
      const removeTimer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
        timersRef.current.delete(id)
      }, TOAST_EXIT_MS)
      timersRef.current.set(`${id}-remove`, removeTimer)
    }, TOAST_DURATION_MS)
    timersRef.current.set(id, exitTimer)
  }, [])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <ToastViewport toasts={toasts} />
    </ToastContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- 한 파일에 Provider + hook 응집. HMR 손상은 webview 환경에서 무관
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast는 ToastProvider 내부에서만 사용 가능합니다')
  return ctx
}

function ToastViewport({ toasts }: { toasts: ToastItem[] }) {
  return (
    <ToastViewportContainer>
      {toasts.map((toast) => (
        <ToastItemElement
          key={toast.id}
          exiting={toast.exiting ?? false}
        >
          {toast.message}
        </ToastItemElement>
      ))}
    </ToastViewportContainer>
  )
}
